/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {Link, nullify, ResponseData, stopPropagation} from '@dvrd/dvr-controls';

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {Trans, useTranslation} from 'react-i18next';

import {useNavigate} from 'react-router';

import Account from '../../models/accountModel';

import EmailStep from '../../models/emailStepModel';

import EmailTemplate from '../../models/emailTemplateModel';

import {EMAIL_TEMPLATE_ROUTE} from '../../util/constants';

import {onSetCrumbs} from '../../util/events';

import {ActivateHandle, EmailStatLoader, EmailStepStatTotal} from '../../util/interfaces';

import {setNavigationParam} from '../../util/navigateUtil';

import {PermissionCallback} from '../../util/pageChanger';

import {
    copyEmailSteps,
    getEmailStepStatsTotal,
    getGlobalEmailStats,
    getHasPlannedEmails,
    sendHistoryMails,
    updateAllEmailSteps,
    validateEmailSteps
} from '../../util/requestSender';

import {createErrorMessage} from '../../util/utils';

import {trackPlatformAction} from '../../util/analytics';

import AccountSelectController from '../accountSelect/accountSelectController';

import {AppContext} from '../misc/appContext';

import PageChangePermission from '../misc/pageChangePermission';

import EmailStepFlow from './emailFlow';

import FlowSelectController from './flow/flowSelectController';

import EmailStepStatsController from './stats/emailStepStatsController';

import TemplateSelectController from './template/templateSelectController';

import CancelEmails from './cancelEmails/cancelEmails';

import {EmailStepError} from '../../util/types';

import EmailFlowErrors from './emailFlowErrors';

import {ProductSelectionEnum} from '../../util/enums';

import {showDialog, showSnackbar} from '@dvrd/dvr-controls';

const maxStatLoaders = 5;

export default function EmailFlowController() {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const [steps, setSteps] = useState<EmailStep[]>([]);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [warnChange, setWarnChange] = useState(false);
    const [templateNavigation, setTemplateNavigation] = useState(false);
    const [search, setSearch] = useState('');
    const [globalStats, setGlobalStats] = useState<EmailStepStatTotal>({SENT: -1, CLICK: -1, OPEN: -1});
    const [hasPlannedEmails, setHasPlannedEmails] = useState(false);
    const [errors, setErrors] = useState<Array<EmailStepError>>([]);

    const stepsLoaded = useRef(false);
    const templatesLoaded = useRef(false);
    const statsRef = useRef<ActivateHandle>(null);
    const flowSelectRef = useRef<ActivateHandle>(null);
    const templateRef = useRef<ActivateHandle>(null);
    const copyRef = useRef<ActivateHandle>(null);
    const loadingStepStats = useRef<Array<EmailStatLoader>>([]);
    const loadingStatsCount = useRef<number>(0);
    const cancelEmailsRef = useRef<ActivateHandle>(null);
    const flowErrorsRef = useRef<ActivateHandle>(null);

    const hasInactive = useMemo(() => steps.find((step: EmailStep) => !step.active), [steps]);
    const enabled = useMemo(() => steps.find((step: EmailStep) => step.active) !== undefined, [steps]);

    const navigate = useNavigate();

    function onLoadStepStats(loader: EmailStatLoader) {
        if (!account) return;
        if (loadingStatsCount.current >= maxStatLoaders) return loadingStepStats.current.push(loader);
        loadStepStats(loader);
    }

    function onChangeSearch(value: string) {
        setSearch(value);
    }

    function onSearch() {
        loadSteps();
    }

    function onClickImport() {
        copyRef.current?.activate();
    }

    function onImport(accountId: string) {
        if (!accountId.length || !account) return;
        setLoading(true);
        copyEmailSteps({
            data: {
                account_id: account.id,
                from_account_id: accountId
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    account.emailSteps = data.email_steps.map((em: any) => new EmailStep(em));
                    context.onChangeContext({account});
                    showSnackbar(t('email.flow.copied', 'De e-mails zijn gekopieerd'));
                    copyRef.current?.close?.();
                    loadData();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ?? t('email.flow.copyFailedMsg', 'Het kopiëren van de e-mails is niet gelukt.')
                        ),
                        t('email.flow.copyFailedTitle', 'Kopiëren mislukt')
                    );
            }
        });
    }

    function onClickTemplate(step: EmailStep) {
        return function () {
            if (allowChange(step)) templateRef.current?.activate(step, step.templateId !== '');
        };
    }

    function onSelectFlow(step: EmailStep) {
        step.deleteFlex('new');
        onReplaceStep(step);
        window.setTimeout(onClickTemplate(step));
    }

    function onReplaceStep(step: EmailStep) {
        step = step.clone();
        setSteps((currentSteps: Array<EmailStep>) => {
            const emailSteps = currentSteps.slice();
            let found: boolean = false;
            for (let i = 0; i < emailSteps.length; i++) {
                if (emailSteps[i].id == step.id) {
                    emailSteps.splice(i, 1, step);
                    found = true;
                    break;
                }
            }
            if (!found) emailSteps.push(step);
            return emailSteps;
        });
    }

    function onDeleteStep(step: EmailStep) {
        setSteps(steps.filter((stp: EmailStep) => stp.id !== step.id));
    }

    function onOpenStats(step?: EmailStep) {
        return function () {
            statsRef.current?.activate(step);
        };
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        onChangeFlowActive(false, () => {
            callback(id, true);
        })(true);
    }

    function onChangeFlowActive(policyConfirmed: boolean = false, callback?: VoidFunction): any {
        return function (active: boolean) {
            const {account} = context.accountContext,
                {user} = context.userContext;
            if (!account || !user) return;
            const accountId = account.id;
            if (active && !policyConfirmed) return confirmPolicy(callback);
            setLoading(true);
            updateSteps(active, accountId, callback);
        };
    }

    function onAddStep() {
        flowSelectRef.current?.activate();
    }

    function onConfirmCloseSendHistoryMails(evt: React.MouseEvent) {
        stopPropagation(evt);
        showDialog(
            <p>
                <Trans i18nKey='email.flow.closeHistoryIntro'>
                    Wij bieden hier de optie om ook <b>eenmalig</b> de e-mails te versturen naar orders van de afgelopen
                    30 dagen.
                </Trans>
                <br />
                <br />
                {t('email.flow.closeHistoryQuestion', 'Weet je zeker dat je hier geen gebruik van wilt maken?')}
                <br />
                {t('email.flow.closeHistoryConsequence', 'Dan zal deze knop verdwijnen.')}
                <br />
                <br />
                {t(
                    'email.flow.closeHistoryHint',
                    "Voor nieuwe orders kun je de e-mails laten inplannen door simpelweg rechtsboven de knop 'Automatisch mailen' op AAN te zetten."
                )}
                <br />
            </p>,
            t('email.flow.closeHistoryTitle', 'Eenmalige mail optie sluiten'),
            [
                {label: t('email.flow.noButton', 'Nee'), primary: false},
                {label: t('email.flow.yesButton', 'Ja'), onClick: onCloseSendHistoryMails}
            ]
        );
    }

    function onCloseSendHistoryMails() {
        const {account} = context.accountContext;
        if (!account) return;
        account.setFlex('history_mails_denied', true);
        context.onChangeContext({account});
        account.update(undefined, undefined, undefined, (account: Account) => {
            context.onChangeContext({account});
        });
    }

    function onConfirmHistoryMails() {
        if (errors.length) {
            return showDialog(
                <p>
                    {t(
                        'email.flow.policyFailBody1',
                        'Je e-mails voldoen niet aan de beleidspunten van Bol. Er worden meerdere e-mails naar dezelfde klanten verzonden of de factuur is niet als bijlage ingesteld, volgens het beleid van Bol mag iedere klant maar één e-mail ontvangen en moet de factuur als bijlage worden toegevoegd.'
                    )}
                    <br />
                    <br />
                    {t(
                        'email.flow.policyFailBody2',
                        'De eenmalige e-mails kunnen alleen worden verzonden als er wordt voldaan aan de beleidspunten.'
                    )}
                    <br />
                    {t(
                        'email.flow.policyFailBody3',
                        'Pas je e-mails aan zodat er maar één mail per klant wordt verstuurd.'
                    )}
                </p>,
                t('email.flow.policyFailTitle', 'Beleidspunten')
            );
        } else if (!enabled) {
            return showDialog(
                <p>
                    {t(
                        'email.flow.enableMailingBody',
                        'Zet eerst het automatisch mailen AAN zodat de e-mails ook zal worden verstuurd naar nieuwe orders.'
                    )}
                </p>,
                t('email.flow.enableMailingTitle', 'Zet mailen aan')
            );
        }
        showDialog(
            <div>
                <p>
                    <Trans i18nKey='email.flow.historyMailsIntro'>
                        Wij bieden hier de optie om <b>eenmalig</b> de e-mails te versturen naar orders van de afgelopen
                        30 dagen.
                    </Trans>
                    <br />
                    <br />
                    {t(
                        'email.flow.historyMailsThinkCarefully',
                        'Denk goed na of de inhoud van jouw e-mail klopt. Deze actie is eenmalig uit te voeren en na uitvoeren verdwijnt deze knop voor altijd.'
                    )}
                    <br />
                    <br />
                    <Trans i18nKey='email.flow.historyMailsNewOrders'>
                        Je kunt de e-mails laten inplannen voor <b>nieuwe orders</b> door simpelweg rechtsboven de knop
                        'Automatisch mailen' op AAN te zetten.
                    </Trans>
                    <br />
                    {t(
                        'email.flow.historyMailsNowOn',
                        'Dit heb je nu aan staan, dus deze mails zullen worden ingepland.'
                    )}
                    <br />
                    <br />
                    {t('email.flow.historyMailsUnderstand', 'Ik begrijp dat...')}
                    <br />
                    <br />
                </p>
                <ul>
                    <li>
                        {t(
                            'email.flow.historyMailsBullet1',
                            'Deze actie eenmalig de e-mails verstuurt voor orders van de afgelopen 30 dagen.'
                        )}
                    </li>
                    <li>{t('email.flow.historyMailsBullet2', 'Deze actie na akkoord niet terug te draaien is.')}</li>
                    <li>
                        {t(
                            'email.flow.historyMailsBullet3',
                            'Voor nieuwe orders de e-mails worden ingepland zoals ingesteld als rechtsboven de automatische verzending van de e-mails op AAN staat.'
                        )}
                    </li>
                </ul>
            </div>,
            t('email.flow.historyMailsTitle', 'E-mails voor oude orders verzenden'),
            [
                {label: t('email.flow.noButton', 'Nee'), primary: false},
                {
                    label: t('email.flow.historyMailsConfirm', 'Ja, ik begrijp het & verzenden'),
                    onClick: onSendHistoryMails
                }
            ]
        );
    }

    function onSendHistoryMails() {
        if (!enabled || errors.length) return;
        const {account} = context.accountContext;
        if (!account) return;
        setLoading(true);
        sendHistoryMails({
            account_id: account.id,
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    context.onChangeContext({account: new Account(data.account)});
                    showDialog(
                        t(
                            'email.flow.scheduled',
                            'De e-mails worden verzonden voor de orders van de afgelopen 30 dagen.'
                        ),
                        t('email.flow.scheduledTitle', 'E-mails worden ingepland')
                    );
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'email.flow.sendHistoryFailed',
                                    'Het verzenden van de e-mails voor de orders van de afgelopen 30 dagen is niet gelukt.'
                                )
                        ),
                        t('email.flow.sendHistoryFailedTitle', 'Verzenden mislukt')
                    );
            }
        });
    }

    function onCancelEmails() {
        cancelEmailsRef.current?.activate();
    }

    function onActivationChanged() {
        /**
         * One or more steps have changed their active state, which could affect the amount of products selected
         * for `REMAINING` mails. Reload all mails with product_selection `REMAINING`
         **/
        for (let ix = 0; ix < steps.length; ix++) {
            const step = steps[ix];
            if (step.productSelection !== ProductSelectionEnum.REMAINING) continue;
            EmailStep.get(step.accountId, step.id, (reloaded: EmailStep, success: boolean) => {
                if (success) onReplaceStep(reloaded);
            });
        }
    }

    function loadStepStats(params: EmailStatLoader) {
        loadingStepStats.current = loadingStepStats.current.filter(
            (loader: EmailStatLoader) => loader.id !== params.id
        );
        if (!account) return;
        loadingStatsCount.current += 1;
        getEmailStepStatsTotal({
            email_step_id: params.id,
            account_id: account.id,
            callback: statsLoaderCallback(params.callback)
        });
    }

    function statsLoaderCallback(callback: (data: ResponseData) => void) {
        return function (data: ResponseData) {
            loadingStatsCount.current -= 1;
            callback(data);
            if (loadingStepStats.current.length) loadStepStats(loadingStepStats.current[0]);
        };
    }

    function updateSteps(active: boolean, accountId: string, callback?: VoidFunction) {
        setLoading(true);
        if (active) saveAccountSetupProgress();
        updateAllEmailSteps({
            data: {
                account_id: accountId,
                email_steps: steps.map((step: EmailStep) => ({email_step_id: step.id, active, account_id: accountId}))
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    setSteps(data.email_steps.map((stp: any) => new EmailStep(stp)));
                    trackPlatformAction('emails', 'save_flow');
                    callback?.();
                } else {
                    const action = active ? 'inschakelen' : 'uitschakelen';
                    const errors = data.errors;
                    if (errors) flowErrorsRef.current?.activate(errors, action);
                    else {
                        const failedMsg = active
                            ? t('email.flow.enableFailed', 'Het inschakelen van de e-mail is niet gelukt.')
                            : t('email.flow.disableFailed', 'Het uitschakelen van de e-mail is niet gelukt.');
                        const failedTitle = active
                            ? t('email.flow.enableFailedTitle', 'Inschakelen mislukt')
                            : t('email.flow.disableFailedTitle', 'Uitschakelen mislukt');
                        showDialog(createErrorMessage(data.message ?? failedMsg), failedTitle);
                    }
                }
            }
        });
    }

    function saveAccountSetupProgress() {
        const {account} = context.accountContext;
        if (!account || account.setupProgress.mail) return;
        account.setupProgress.mail = true;
        account.update(undefined, undefined, undefined, (account: Account, success: boolean) => {
            if (success) context.onChangeContext({account});
        });
    }

    function confirmPolicy(callback?: VoidFunction) {
        if (errors.length) {
            const message = (
                <label>
                    {t('email.flow.bolPolicyPrefix', 'Eén of meer stappen voldoen niet aan het ')}
                    <Link route='https://partnerplatform.bol.com/nl/hulp-nodig/prestaties/beleidspunten-zelfpromotie/'>
                        {t('email.flow.bolPolicyLink', 'beleid van Bol')}
                    </Link>
                    {t(
                        'email.flow.bolPolicySuffix',
                        '. Wil je de automatische verzending van de e-mails toch activeren?'
                    )}
                </label>
            );
            showDialog(message, t('email.flow.bolPolicyTitle', 'Bol beleid'), [
                t('email.flow.noButton', 'Nee'),
                {
                    label: t('email.flow.yesButton', 'Ja'),
                    primary: false,
                    onClick: () => {
                        onChangeFlowActive(true, callback)(true);
                    }
                }
            ]);
        } else onChangeFlowActive(true, callback)(true);
    }

    function loadSteps() {
        const {account} = context.accountContext;
        if (!account) {
            stepsLoaded.current = true;
            return finishLoading();
        }
        setLoading(true);
        stepsLoaded.current = false;
        EmailStep.getAll(account.id, nullify(search), (steps: EmailStep[], success: boolean, data: ResponseData) => {
            if (success) {
                setSteps(steps);
                loadTemplates();
            } else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('email.flow.loadFailed', 'Het laden van de e-mails is niet gelukt.')
                    ),
                    t('email.flow.loadFailedTitle', 'Laden mislukt')
                );
            stepsLoaded.current = true;
            finishLoading();
            window.setTimeout(scrollToNew);
        });
    }

    function loadGlobalStats() {
        if (!account) return;
        getGlobalEmailStats({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (!data.success) return;
                const {sent_count, open_count, click_count} = data;
                setGlobalStats({SENT: sent_count, OPEN: open_count, CLICK: click_count});
            }
        });
    }

    function loadHasPlannedEmails() {
        if (!account) return;
        getHasPlannedEmails({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (!data.success) return;
                setHasPlannedEmails(data.has_planned_emails);
            }
        });
    }

    function loadTemplates() {
        const {account} = context.accountContext;
        if (!account) {
            templatesLoaded.current = true;
            return finishLoading();
        }
        templatesLoaded.current = false;
        EmailTemplate.getAll(account.id, -1, -1, (templates: EmailTemplate[], success: boolean, data: ResponseData) => {
            if (success) setTemplates(templates);
            else
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t('email.flow.loadTemplatesFailed', 'Het laden van de e-mail templates is niet gelukt.')
                    ),
                    t('email.flow.loadFailedTitle', 'Laden mislukt')
                );
            templatesLoaded.current = true;
            finishLoading();
        });
    }

    function loadData() {
        loadSteps();
        loadGlobalStats();
        loadHasPlannedEmails();
    }

    function finishLoading() {
        if (stepsLoaded.current && templatesLoaded.current) setLoading(false);
    }

    function allowChange(step: EmailStep, boolOnly: boolean = false) {
        if (!step.active) return true;
        if (context.userContext.user?.isAdmin()) return true;
        if (boolOnly) return !enabled;
        if (enabled) {
            setWarnChange(true);
            showDialog(
                <label>
                    <Trans i18nKey='email.flow.pauseToChangeMsg'>
                        Pauzeer eerst <b>rechtsboven</b> het verzenden van mails om wijzingen door te voeren.
                    </Trans>
                </label>,
                t('email.flow.pauseToChangeTitle', 'Stap wijzigen')
            );
            return false;
        }
        return true;
    }

    function scrollToNew() {
        const newElement = document.querySelector('div.email-step.new');
        if (!newElement) return;
        newElement.scrollIntoView({behavior: 'smooth'});
    }

    function onNavigateTemplate(template: EmailTemplate, step: EmailStep, returnPage?: string) {
        setTemplateNavigation(true);
        window.setTimeout(() => {
            setNavigationParam({template, step, returnPage});
            navigate(EMAIL_TEMPLATE_ROUTE);
        });
    }

    function validateSteps() {
        if (!account) return;
        validateEmailSteps({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (data.success) setErrors(data.errors);
            }
        });
    }

    useEffect(() => {
        onSetCrumbs([{label: t('email.flow.crumb', 'E-mails')}]);
    }, [t]);

    useEffect(() => {
        loadData();
    }, [context.accountContext.account]);

    useEffect(() => {
        validateSteps();
    }, [steps]);

    return (
        <>
            <EmailStepFlow
                onReplaceStep={onReplaceStep}
                onDeleteStep={onDeleteStep}
                onOpenStats={onOpenStats}
                onChangeActive={onChangeFlowActive()}
                onClickTemplate={onClickTemplate}
                onSendHistoryMails={onConfirmHistoryMails}
                onClickImport={onClickImport}
                onCloseSendHistoryMails={onConfirmCloseSendHistoryMails}
                onCancelEmails={onCancelEmails}
                allowChange={allowChange}
                loading={loading}
                steps={steps}
                templates={templates}
                flowIsActive={enabled}
                warnChange={warnChange}
                onAddStep={onAddStep}
                onGlobalLoad={setLoading}
                onNavigateTemplate={onNavigateTemplate}
                search={search}
                onChangeSearch={onChangeSearch}
                onSearch={onSearch}
                globalStats={globalStats}
                onLoadStepStats={onLoadStepStats}
                onActivationChanged={onActivationChanged}
                hasPlannedEmails={hasPlannedEmails}
                errors={errors}
            />
            <EmailStepStatsController ref={statsRef} />
            <FlowSelectController onReplaceStep={onSelectFlow} ref={flowSelectRef} />
            <TemplateSelectController
                onReplaceStep={onReplaceStep}
                onNavigateTemplate={onNavigateTemplate}
                ref={templateRef}
            />
            <AccountSelectController
                ref={copyRef}
                loading={loading}
                onImport={onImport}
                title={t('email.flow.importTitle', 'E-mails importeren')}
            >
                <p className='import-text'>
                    {t('email.flow.importAllFromAccount', 'Alle e-mails uit een ander account ophalen.')}
                    <br />
                    <br />
                    <b>{t('email.flow.importWarningLabel', 'Let op: ')}</b>
                    {t('email.flow.importWarningText', 'alle huidige emails worden overschreven!')}
                    <br />
                    <br />
                    {t('email.flow.importEansOnly', "Alleen bestaande EAN's worden meegenomen.")}
                </p>
            </AccountSelectController>
            <CancelEmails ref={cancelEmailsRef} />
            <EmailFlowErrors ref={flowErrorsRef} />
            <PageChangePermission
                changed={!templateNavigation && steps.length > 0 && !!hasInactive}
                onDeny={onDenyPageChange}
                denyText={t('email.flow.pageChangeDeny', 'Ja, verzending inschakelen')}
                allowText={t('email.flow.pageChangeAllow', 'Nee, pagina verlaten')}
                allowSecondary={true}
                title={t('email.flow.pageChangeTitle', 'Automatische verzending e-mails')}
            >
                {t(
                    'email.flow.pageChangeBody',
                    'De automatische verzending van sommige e-mails is niet ingeschakeld, sommige e-mails worden dus niet automatisch verzonden. Wil je de automatische verzending van de e-mails inschakelen voordat je de pagina verlaat?'
                )}
            </PageChangePermission>
        </>
    );
}
